import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft, ArrowRight, LifeBuoy, Lock, Mail, User } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import ProductPreview from '../components/ProductPreview';
import type { ApiError, OrderProductSummary, ProductDetail } from '../lib/api';
import { formatPrice } from '../lib/format';
import { useCreateOrder, useProduct } from '../lib/queries';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Řádek doručení do souhrnu podle způsobu plnění. */
function deliveryLine(
  p: Pick<ProductDetail | OrderProductSummary, 'content_type' | 'fulfillment_type'>,
) {
  switch (p.fulfillment_type) {
    case 'STREAM':
      return 'Přístup ke streamu obvykle během pár minut po zaplacení';
    case 'LICENSE_KEY':
      return 'Licenční klíč e-mailem obvykle během pár minut po zaplacení';
    case 'DOWNLOAD':
      return 'Odkaz na stránku objednávky ke stažení přijde e-mailem obvykle během pár minut po zaplacení';
    case 'EMAIL':
    default:
      return `${p.content_type === 'PDF' ? 'PDF' : 'Obsah'} e-mailem obvykle během pár minut po zaplacení`;
  }
}

function CheckoutSkeleton() {
  return (
    <div className="mx-auto max-w-2xl space-y-5 px-4 py-6">
      <div className="h-24 rounded-card bg-paper2 motion-safe:animate-pulse" />
      <div className="h-14 rounded-card bg-paper2 motion-safe:animate-pulse" />
      <div className="h-40 rounded-card bg-paper2 motion-safe:animate-pulse" />
    </div>
  );
}

export default function CheckoutNew() {
  const [params] = useSearchParams();
  const slug = params.get('produkt') ?? undefined;
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  const { data: product, isLoading, isError } = useProduct(slug);
  const createOrder = useCreateOrder();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentDigital, setConsentDigital] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  const nameValid = name.trim().length >= 3;
  const nameError = nameTouched && name.length > 0 && !nameValid;
  const emailValid = EMAIL_RE.test(email.trim());
  const emailError = emailTouched && email.length > 0 && !emailValid;

  if (isLoading) {
    return (
      <div className="grid-paper min-h-[70vh]">
        <CheckoutSkeleton />
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="grid-paper min-h-[70vh]">
        <div className="mx-auto max-w-2xl px-4 py-20 text-center">
          <h1 className="text-3xl">Materiál nenalezen</h1>
          <p className="mt-3 text-ink-soft">
            {slug
              ? 'Tento materiál se nepodařilo načíst. Vyberte prosím produkt z katalogu.'
              : 'Chybí vybraný produkt. Vyberte materiál z katalogu.'}
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex h-12 items-center rounded-xl bg-board px-6 font-semibold text-chalk transition hover:bg-board-deep"
          >
            Zpět do katalogu
          </Link>
        </div>
      </div>
    );
  }

  const canSubmit =
    nameValid && emailValid && consentTerms && consentDigital && !createOrder.isPending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setNameTouched(true);
    setEmailTouched(true);
    if (!nameValid) {
      toast.error('Zadejte jméno a příjmení', { description: 'Uvedeme je na dokladu.' });
      return;
    }
    if (!emailValid) {
      toast.error('Zadejte platný e-mail', { description: 'Sem doručíme váš nákup.' });
      return;
    }
    if (!consentTerms) {
      toast.error('Potvrďte prosím souhlas s obchodními podmínkami');
      return;
    }
    if (!consentDigital) {
      toast.error('Potvrďte prosím souhlas se zpřístupněním obsahu ihned');
      return;
    }

    createOrder.mutate(
      {
        product_slug: product.slug,
        email: email.trim(),
        buyer_name: name.trim(),
        consent_terms: consentTerms,
        consent_digital_delivery: consentDigital,
      },
      {
        onSuccess: (order) => navigate(`/pokladna/${order.token}`),
        onError: (err) => {
          const e = err as ApiError;
          toast.error('Objednávku se nepodařilo vytvořit', {
            description: e?.message ?? 'Zkuste to prosím znovu.',
          });
        },
      },
    );
  };

  const entrance = reduce
    ? {}
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.25 },
      };

  return (
    <div className="grid-paper min-h-[70vh] pb-16">
      <motion.form onSubmit={submit} className="mx-auto max-w-2xl px-4 py-5" {...entrance}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <Link
            to={`/produkt/${product.slug}`}
            className="inline-flex h-11 items-center gap-1.5 rounded-xl px-2 text-sm font-medium text-ink-soft transition hover:text-ink"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
            Zpět na produkt
          </Link>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-soft">
            <Lock className="h-3.5 w-3.5" strokeWidth={2} />
            Zabezpečený nákup
          </span>
        </div>

        <h1 className="text-[26px] leading-tight md:text-3xl">Pokladna</h1>

        {/* Souhrn objednávky */}
        <section className="mt-4 flex items-center gap-4 rounded-card border border-line bg-card p-4 shadow-card">
          <span className="board grid h-16 w-16 flex-none place-items-center overflow-hidden rounded-xl">
            <ProductPreview
              subject={product.subject}
              contentType={product.content_type}
              productId={product.id}
              className="h-auto w-[78%]"
            />
          </span>
          <div className="min-w-0 grow">
            <p className="font-semibold leading-snug">{product.title}</p>
            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-ink-soft">
              <Mail className="h-4 w-4 flex-none" strokeWidth={2} />
              {deliveryLine(product)}
            </p>
          </div>
          <p className="shrink-0 text-xl font-bold tabular-nums text-ink">
            {formatPrice(product.price_czk)}
          </p>
        </section>

        {/* Jméno a příjmení */}
        <section className="mt-5">
          <label htmlFor="name" className="block text-sm font-semibold text-ink">
            Jméno a příjmení pro doklad
          </label>
          <div className="relative mt-2">
            <User
              className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-soft"
              strokeWidth={2}
            />
            <input
              id="name"
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setNameTouched(true)}
              placeholder="Jan Novák"
              aria-invalid={nameError}
              className={`h-[52px] w-full rounded-xl border bg-card pl-12 pr-4 text-base outline-none transition focus:ring-2 ${
                nameError
                  ? 'border-error focus:border-error focus:ring-error/30'
                  : 'border-line focus:border-board focus:ring-board/25'
              }`}
            />
          </div>
          {nameError ? (
            <p className="mt-1.5 text-xs font-medium text-error">
              Zadejte prosím jméno a příjmení.
            </p>
          ) : null}
        </section>

        {/* E-mail */}
        <section className="mt-5">
          <label htmlFor="email" className="block text-sm font-semibold text-ink">
            E-mail pro doručení
          </label>
          <div className="relative mt-2">
            <Mail
              className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-ink-soft"
              strokeWidth={2}
            />
            <input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              placeholder="jmeno@email.cz"
              aria-invalid={emailError}
              className={`h-[52px] w-full rounded-xl border bg-card pl-12 pr-4 text-base outline-none transition focus:ring-2 ${
                emailError
                  ? 'border-error focus:border-error focus:ring-error/30'
                  : 'border-line focus:border-board focus:ring-board/25'
              }`}
            />
          </div>
          {emailError ? (
            <p className="mt-1.5 text-xs font-medium text-error">
              Zkontrolujte prosím tvar e-mailu.
            </p>
          ) : (
            <p className="mt-1.5 text-xs text-ink-soft">Na tento e-mail pošleme nákup i doklad.</p>
          )}
        </section>

        {/* Souhlasy */}
        <section className="mt-6 space-y-3">
          <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={consentTerms}
              onChange={(e) => setConsentTerms(e.target.checked)}
              className="mt-0.5 h-5 w-5 flex-none rounded border-line accent-[#20433A]"
            />
            <span className="text-sm leading-relaxed text-ink-soft">
              Souhlasím s{' '}
              <Link
                to="/obchodni-podminky"
                className="font-semibold text-board underline decoration-board/30 underline-offset-2"
              >
                obchodními podmínkami
              </Link>{' '}
              a beru na vědomí{' '}
              <Link
                to="/ochrana-osobnich-udaju"
                className="font-semibold text-board underline decoration-board/30 underline-offset-2"
              >
                zásady zpracování osobních údajů
              </Link>
              .
            </span>
          </label>

          <div className="rounded-card border border-line bg-paper2 p-4">
            <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={consentDigital}
                onChange={(e) => setConsentDigital(e.target.checked)}
                className="mt-0.5 h-5 w-5 flex-none rounded border-line accent-[#20433A]"
              />
              <span className="text-sm font-medium leading-relaxed text-ink">
                Žádám o zpřístupnění digitálního obsahu ihned po zaplacení, tj. před uplynutím
                14denní lhůty pro odstoupení od smlouvy. Beru na vědomí, že tímto souhlasem
                v&nbsp;okamžiku zahájení plnění ztrácím právo na odstoupení od smlouvy.
              </span>
            </label>
            <p className="mt-2 pl-8 text-xs leading-relaxed text-ink-soft">
              Digitální materiály posíláme okamžitě, proto u&nbsp;nich ze zákona zaniká možnost
              vrácení. Bez tohoto souhlasu nelze objednávku dokončit. Podrobnosti najdete v{' '}
              <Link
                to="/obchodni-podminky"
                className="font-semibold text-board underline underline-offset-2"
              >
                Obchodních podmínkách
              </Link>{' '}
              a v{' '}
              <Link
                to="/odstoupeni-od-smlouvy"
                className="font-semibold text-board underline underline-offset-2"
              >
                Odstoupení od smlouvy
              </Link>
              .
            </p>
          </div>
        </section>

        {/* CTA, zašedlé, dokud nejsou vyplněné údaje a zaškrtnuté oba souhlasy. */}
        <button
          type="submit"
          disabled={!canSubmit}
          aria-disabled={!canSubmit}
          className="mt-6 flex h-[52px] w-full items-center justify-center gap-2 rounded-xl bg-board font-semibold text-chalk shadow-card transition hover:bg-board-deep active:scale-[.98] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          {createOrder.isPending ? (
            'Připravuji objednávku…'
          ) : (
            <>
              Přejít k platbě
              <ArrowRight className="h-5 w-5" strokeWidth={2} />
            </>
          )}
        </button>
        {!consentTerms || !consentDigital ? (
          <p className="mt-2 text-center text-xs text-ink-soft">
            Pro pokračování zaškrtněte oba souhlasy výše.
          </p>
        ) : null}

        {/* Pomoc */}
        <section className="mt-5 flex items-start gap-3 rounded-card border border-line bg-card p-4">
          <LifeBuoy className="mt-0.5 h-5 w-5 flex-none text-info" strokeWidth={2} />
          <p className="text-sm leading-relaxed text-ink-soft">
            Nedaří se dokončit nákup? Napište mi na{' '}
            <a href="mailto:pf.kabinet.napadu@gmail.com" className="font-semibold text-board">
              pf.kabinet.napadu@gmail.com
            </a>
            , ozvu se rychle.
          </p>
        </section>
      </motion.form>
    </div>
  );
}
